/* eslint-disable no-console */
/**
 * PostHog Service - Singleton service for managing PostHog feature flags
 * Simplified for admin portal: feature flags only, no event tracking or session recording
 */

import posthog from 'posthog-js';

class PosthogService {
  static instance = null;

  posthogInstance = null;

  initialized = false;

  flagsReady = false;

  flagsReadyPromise = null;

  flagsReadyResolver = null;

  config = null;

  // Tracks the last reported value for each flag key. With
  // send_event: false on SDK calls, we manually fire
  // $feature_flag_called once per flag key per session AND again
  // whenever the resolved value changes (e.g. after a flag reload).
  flagCallReported = {};

  static getInstance() {
    if (!PosthogService.instance) {
      PosthogService.instance = new PosthogService();
    }
    return PosthogService.instance;
  }

  getPosthogInstance() {
    return this.posthogInstance;
  }

  initialize(config) {
    if (this.initialized || config.disabled || !config.apiKey) {
      if (config.debug) {
        console.log('[PostHog] Initialization skipped', {
          initialized: this.initialized,
          disabled: config.disabled,
          hasApiKey: !!config.apiKey,
        });
      }
      return;
    }

    this.config = config;

    try {
      posthog.init(config.apiKey, {
        api_host: config.apiHost || 'https://us.i.posthog.com',
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        disable_surveys: true,
        enable_heatmaps: false,
        debug: config.debug,
        persistence: 'localStorage+cookie',
        // Cookie is scoped to this subdomain only. Without this, the PostHog cookie is set on
        // the parent domain (e.g. .tempdee.com) and shared with dental-portal, which causes
        // Person records to merge across apps when admins sign into one portal after the other.
        cross_subdomain_cookie: false,
      });

      posthog.register({
        environment: process.env.REACT_APP_TEMP_MEE_ENV || 'development',
        appType: 'admin_portal',
      });

      posthog.people.set({
        environment: process.env.REACT_APP_TEMP_MEE_ENV || 'development',
        appType: 'admin_portal',
      });

      this.posthogInstance = posthog;
      window.posthog = posthog;
      this.initialized = true;

      this.flagsReadyPromise = new Promise((resolve) => {
        this.flagsReadyResolver = resolve;
      });

      this.posthogInstance.onFeatureFlags(() => {
        this.flagsReady = true;
        if (this.flagsReadyResolver) {
          this.flagsReadyResolver();
          this.flagsReadyResolver = null;
        }
        if (config.debug) {
          console.log('[PostHog] Feature flags loaded and ready');
        }
      });
    } catch (error) {
      console.error('[PostHog] Initialization error:', error);
    }
  }

  identify(userId, traits) {
    if (!this.isReady()) return;

    try {
      this.posthogInstance.identify(userId, traits || {});

      if (this.config?.debug) {
        console.log('[PostHog] User identified:', userId, traits);
      }
    } catch (error) {
      console.error('[PostHog] Identify error:', error);
    }
  }

  reset() {
    if (!this.isReady()) return;

    try {
      this.posthogInstance.reset();
      // Allow flag events to fire again for the next user session.
      this.flagCallReported = {};

      if (this.config?.debug) {
        console.log('[PostHog] User reset');
      }
    } catch (error) {
      console.error('[PostHog] Reset error:', error);
    }
  }

  /**
   * Check if a feature flag is enabled.
   * Reports a $feature_flag_called event at most once per flag key per session.
   */
  isFeatureEnabled(flagKey, defaultValue = false) {
    if (!this.isReady()) return defaultValue;

    try {
      const isEnabled = this.posthogInstance.isFeatureEnabled(flagKey, { send_event: false });
      const value = isEnabled ?? defaultValue;

      this.reportFlagCalledOnce(flagKey, value);

      return value;
    } catch (error) {
      console.error('[PostHog] Feature flag error:', error);
      return defaultValue;
    }
  }

  /**
   * Get feature flag variant value.
   * Returns the variant string (e.g., 'control', 'test') or boolean if no variants.
   * Reports a $feature_flag_called event at most once per flag key per session.
   */
  getFeatureFlagVariant(flagKey) {
    if (!this.isReady()) return null;

    try {
      const value = this.posthogInstance.getFeatureFlag(flagKey, { send_event: false }) ?? null;

      this.reportFlagCalledOnce(flagKey, value);

      return value;
    } catch (error) {
      console.error('[PostHog] Feature flag variant error:', error);
      return null;
    }
  }

  getFeatureFlagPayload(flagKey) {
    if (!this.isReady()) return null;

    try {
      return this.posthogInstance.getFeatureFlagPayload(flagKey);
    } catch (error) {
      console.error('[PostHog] Feature flag payload error:', error);
      return null;
    }
  }

  setPersonPropertiesForFlags(properties) {
    if (!this.isReady()) return;

    try {
      this.posthogInstance.setPersonPropertiesForFlags(properties);

      if (this.config?.debug) {
        console.log('[PostHog] Person properties set for flags:', properties);
      }
    } catch (error) {
      console.error('[PostHog] Set person properties error:', error);
    }
  }

  reloadFeatureFlags() {
    if (!this.isReady()) return;

    try {
      this.posthogInstance.reloadFeatureFlags();

      if (this.config?.debug) {
        console.log('[PostHog] Feature flags reloaded');
      }
    } catch (error) {
      console.error('[PostHog] Reload feature flags error:', error);
    }
  }

  onFeatureFlags(callback) {
    if (!this.isReady()) return;

    try {
      this.posthogInstance.onFeatureFlags(callback);
    } catch (error) {
      console.error('[PostHog] Feature flags callback error:', error);
    }
  }

  /**
   * Wait for the next feature flags response from the server.
   * Use this after an identify call that already triggers a flag reload
   * internally — this avoids a redundant /decide API call.
   */
  async waitForFlagReload(timeoutMs = 5000) {
    if (!this.isReady() || !this.posthogInstance) return;

    await new Promise((resolve) => {
      const timeout = setTimeout(resolve, timeoutMs);

      this.posthogInstance.onFeatureFlags(() => {
        clearTimeout(timeout);
        resolve();
      });
    });
  }

  /**
   * Sends a $feature_flag_called event once per flag key per session,
   * and again if the resolved value changes (e.g. after a server-side toggle).
   * This replaces the SDK's built-in reporting (disabled via send_event: false)
   * to avoid the dedup reset that happens on every flag reload.
   *
   * Public so FeatureFlagManager can share the same dedup map instead of
   * maintaining a duplicate one.
   */
  reportFlagCalledOnce(flagKey, value) {
    const dedupKey =
      typeof value === 'string' ? `variant:${flagKey}` : `bool:${flagKey}`;

    if (
      dedupKey in this.flagCallReported &&
      this.flagCallReported[dedupKey] === value
    ) {
      return;
    }

    this.flagCallReported[dedupKey] = value;

    const details =
      this.posthogInstance?.featureFlags?.getFeatureFlagDetails(flagKey);

    this.posthogInstance?.capture('$feature_flag_called', {
      $feature_flag: flagKey,
      $feature_flag_response: value,
      ...(details?.metadata?.id != null && {
        $feature_flag_id: details.metadata.id,
      }),
      ...(details?.metadata?.version != null && {
        $feature_flag_version: details.metadata.version,
      }),
      ...((details?.reason?.description ?? details?.reason?.code) && {
        $feature_flag_reason:
          details.reason?.description ?? details.reason?.code,
      }),
    });

    if (this.config?.debug) {
      console.log(`[PostHog] Feature flag '${flagKey}':`, value);
    }
  }

  areFlagsReady() {
    return this.flagsReady && this.isReady();
  }

  async waitForFlags() {
    if (!this.isReady()) {
      if (this.config?.disabled) {
        return;
      }
      throw new Error('[PostHog] Service not initialized');
    }

    if (this.flagsReady) {
      return;
    }

    if (this.flagsReadyPromise) {
      await this.flagsReadyPromise;
    }
  }

  isReady() {
    const ready = this.initialized && this.posthogInstance !== null && !this.config?.disabled;

    if (!ready && this.config?.debug) {
      console.log('[PostHog] Not ready:', {
        initialized: this.initialized,
        hasInstance: this.posthogInstance !== null,
        disabled: this.config?.disabled,
      });
    }

    return ready;
  }
}

export default PosthogService;
